import * as dayjs from 'dayjs';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../dto/data-model.js';
import { CustomerOrderBase } from '../../contracts/customer-order.js';
import { MapUtils } from '../../utility/map-utils.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { TimeZone } from '../../time/timezone.js';
import { isSameData } from '../dto/data-model-utils.js';
import { OnlineEventTicketOrder } from './online-event-ticket-order.js';
import { EventTicketOrder } from './event-ticket-order.js';
import { BusinessModuleRef } from './business-module-ref.js';

export enum OrderCompletionRequestStatus {
  Pending = 'PENDING',
  Completed = 'COMPLETED'
}

export enum OrderCompletionMode {
  Abort = 'ABORT',
  Complete = 'COMPLETE'
}

export class OrderCompletionRequest extends DataModel {
  static readonly TYPE_ID: string = '1875f7ea3974427d9ebe83b1392b79b1';

  private constructor(
    public readonly uniqueId: string,
    public readonly status: OrderCompletionRequestStatus,
    public readonly mode: OrderCompletionMode,
    public readonly order: CustomerOrderBase,
    public readonly creationDate: dayjs.Dayjs,
    public readonly completionDate: dayjs.Dayjs | null,
  ) {
    super();
  }

  get $typeId(): string {
    return OrderCompletionRequest.TYPE_ID;
  }

  get creatorId(): string {
    return this.order.creatorId;
  }

  get userId(): string | null {
    return this.order.userId;
  }

  get isGuestOrder(): boolean {
    return this.order.isGuestOrder;
  }

  get businessRef(): BusinessModuleRef {
    return this.order.businessRef;
  }

  get eventId(): string | null {
    return (OnlineEventTicketOrder.assertType(this.order) || EventTicketOrder.assertType(this.order)) ?
        this.order.eventId : null;
  }

  static complete(opts: {
      uniqueId: string,
      order: CustomerOrderBase,
      creationDate: dayjs.Dayjs,
  }): OrderCompletionRequest {
    return new OrderCompletionRequest(
        opts.uniqueId,
        OrderCompletionRequestStatus.Pending,
        OrderCompletionMode.Complete,
        opts.order,
        opts.creationDate,
        null,
    );
  }

  static abort(opts: {
      uniqueId: string,
      order: CustomerOrderBase,
      creationDate: dayjs.Dayjs,
  }): OrderCompletionRequest {
    return new OrderCompletionRequest(
        opts.uniqueId,
        OrderCompletionRequestStatus.Pending,
        OrderCompletionMode.Abort,
        opts.order,
        opts.creationDate,
        null,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): OrderCompletionRequest {
    return new OrderCompletionRequest(
        MapUtils.get<string>(map, 'uniqueId'),
        toEnumFromString(MapUtils.get<string>(map, '$status'), OrderCompletionRequestStatus),
        toEnumFromString(MapUtils.get<string>(map, 'mode'), OrderCompletionMode),
        context.fromData<CustomerOrderBase>(MapUtils.get<SerializedData>(map, 'order')),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        context.decodeDateOrNull(MapUtils.getOrNull<number>(map, 'completionDate')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'orderId': this.order.uniqueId,
      'creatorId': this.creatorId,
      'userId': this.userId,
      'eventId': this.eventId,
      'isGuestOrder': this.isGuestOrder,
      'order': context.toData(this.order),
      'businessRef': this.businessRef.encode(),
      'creationDate': context.encodeDate(this.creationDate),
      'completionDate': context.encodeDateOrNull(this.completionDate),
      '$status': fromEnumAsString(this.status),
      'mode': fromEnumAsString(this.mode),
    };
  }

  clone(): this {
    return new OrderCompletionRequest(
        this.uniqueId,
        this.status,
        this.mode,
        this.order.clone(),
        TimeZone.cloneDate(this.creationDate),
        TimeZone.cloneDateOrNull(this.completionDate),
    ) as this;
  }

  isSame(other: OrderCompletionRequest): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.creationDate === this.creationDate &&
        other.completionDate === this.completionDate &&
        other.status === this.status &&
        other.mode === this.mode &&
        isSameData(other.order, this.order);
  }

  copyAsComplete(completionDate: dayjs.Dayjs): this {
    return new OrderCompletionRequest(
        this.uniqueId,
        OrderCompletionRequestStatus.Completed,
        this.mode,
        this.order.clone(),
        TimeZone.cloneDate(this.creationDate),
        TimeZone.cloneDate(completionDate),
    ) as this;
  }
}

export class OrderCompletionRequestBuilder extends DataModelBuilder<OrderCompletionRequest> {
  build(context: SerializationContext, map: SerializedData): OrderCompletionRequest {
    return OrderCompletionRequest.fromMap(context, map);
  }
}
